import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Clock, FileText, XCircle, type LucideIcon } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { Cargando } from '@/shared/ui/Cargando';
import { Seccion } from '@/shared/ui/Seccion';
import { ListaDeFacturasSri } from './ListaDeFacturasSri';
import { BarrasApiladas, enDinero, enPorcentaje } from '@/shared/ui/graficos';

interface Periodo {
  periodo: string;
  emitidas: number;
  autorizadas: number;
  rechazadas: number;
  enCola: number;
  importe: number;
}

interface Panel {
  emitidas: number;
  autorizadas: number;
  rechazadas: number;
  enCola: number;
  importe: number;
  periodos: Periodo[];
}

const SERIES = [
  { clave: 'autorizadas', nombre: 'Autorizadas', color: '#0E7C5A' },
  { clave: 'enCola', nombre: 'En cola', color: '#F59E0B' },
  { clave: 'rechazadas', nombre: 'Rechazadas', color: '#EF4444' },
] as const;

export function PanelDelSri({ desde, hasta }: { desde: string; hasta: string }) {
  const panel = useQuery({
    queryKey: ['informe-sri', desde, hasta],
    queryFn: () => api.get<Panel>('/informes/facturacion/sri/panel', { query: { desde, hasta } }),
  });

  if (panel.isLoading) {
    return <Cargando titulo="Cargando" detalle="revisando qué llegó al SRI" />;
  }

  const datos = panel.data;
  if (!datos || datos.periodos.length === 0) {
    return (
      <p className="rounded-2xl border border-armazon-200 bg-white p-12 text-center text-sm uppercase text-armazon-400">
        No se emitió nada en el período elegido
      </p>
    );
  }

  const autorizado = datos.emitidas > 0 ? (datos.autorizadas / datos.emitidas) * 100 : 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Cifra
          icono={FileText}
          rotulo="Emitidas"
          valor={datos.emitidas.toLocaleString('es-EC')}
          tono="text-armazon-700"
          fondo="bg-armazon-50"
          ayuda={`Por ${enDinero(datos.importe)}`}
        />
        <Cifra
          icono={CheckCircle2}
          rotulo="Autorizadas"
          valor={`${datos.autorizadas.toLocaleString('es-EC')} · ${enPorcentaje(autorizado)}`}
          tono="text-marca-700"
          fondo="bg-marca-50"
        />
        <Cifra
          icono={Clock}
          rotulo="En cola"
          valor={datos.enCola.toLocaleString('es-EC')}
          tono="text-amber-700"
          fondo="bg-amber-50"
          ayuda="Emitidas que todavía no han llegado al SRI"
        />
        <Cifra
          icono={XCircle}
          rotulo="Rechazadas"
          valor={datos.rechazadas.toLocaleString('es-EC')}
          tono={datos.rechazadas > 0 ? 'text-red-700' : 'text-armazon-500'}
          fondo={datos.rechazadas > 0 ? 'bg-red-50' : 'bg-armazon-50'}
          ayuda="Cada una tiene su motivo en el Proceso SRI"
        />
      </div>

      <Seccion
        titulo="Qué pasó con lo emitido"
        ayuda="Por período facturado. El alto de la barra es todo lo que se emitió ese mes"
      >
        <BarrasApiladas datos={datos.periodos} claveEtiqueta="periodo" series={SERIES} />
      </Seccion>

      <Seccion titulo="Detalle por período" ayuda="Los mismos números del gráfico, con su importe">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-armazon-50/80">
              <tr>
                {['Período', 'Emitidas', 'Autorizadas', 'Rechazadas', 'En cola', 'Importe'].map(
                  (c, i) => (
                    <th
                      key={c}
                      scope="col"
                      className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-armazon-500 ${
                        i === 0 ? 'text-left' : 'text-right'
                      }`}
                    >
                      {c}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-armazon-100">
              {datos.periodos.map((p) => (
                <tr key={p.periodo} className="transition-colors hover:bg-armazon-50/60">
                  <td className="px-3 py-2.5 text-xs font-medium text-armazon-700">{p.periodo}</td>
                  <td className="px-3 py-2.5 text-right text-xs tabular-nums">
                    {p.emitidas.toLocaleString('es-EC')}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs tabular-nums text-marca-700">
                    {p.autorizadas.toLocaleString('es-EC')}
                  </td>
                  <td
                    className={`px-3 py-2.5 text-right text-xs tabular-nums ${
                      p.rechazadas > 0 ? 'font-semibold text-red-700' : 'text-armazon-400'
                    }`}
                  >
                    {p.rechazadas.toLocaleString('es-EC')}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs tabular-nums text-amber-700">
                    {p.enCola.toLocaleString('es-EC')}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs font-semibold tabular-nums">
                    {enDinero(p.importe)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Seccion>

      <Seccion
        titulo="Factura por factura"
        ayuda="Las del sistema anterior y las de SIREPAGAL, con cómo quedaron ante el SRI"
      >
        <ListaDeFacturasSri desde={desde} hasta={hasta} />
      </Seccion>
    </div>
  );
}

function Cifra({
  icono: Icono,
  rotulo,
  valor,
  tono,
  fondo,
  ayuda,
}: {
  icono: LucideIcon;
  rotulo: string;
  valor: string;
  tono: string;
  fondo: string;
  ayuda?: string;
}) {
  return (
    <div className={`rounded-2xl border border-white/60 p-4 ${fondo}`} title={ayuda}>
      <span className="mb-2 flex items-center gap-2">
        <Icono className={`size-4 ${tono}`} aria-hidden />
        <span className="text-[10px] font-bold uppercase tracking-wider text-armazon-500">
          {rotulo}
        </span>
      </span>
      <span className={`block truncate text-xl font-bold tabular-nums ${tono}`}>{valor}</span>
    </div>
  );
}
